<script lang="ts">
	import type { WithElementRef, WithoutChildren } from 'bits-ui'
	import { getChartContext, Tooltip as TooltipPrimitive } from 'layerchart'
	import type { Snippet } from 'svelte'
	import type { HTMLAttributes } from 'svelte/elements'
	import { cn } from 'tailwind-variants'

	import { getPayloadConfigFromPayload, useChart, type TooltipPayload } from './chart-utils.js'

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	function defaultFormatter(value: any) {
		return `${value}`
	}

	let {
		ref = $bindable(null),
		class: className,
		hideLabel = false,
		indicator = 'dot',
		hideIndicator = false,
		hideName = false,
		labelKey,
		label,
		labelFormatter = defaultFormatter,
		labelClassName,
		formatter,
		nameKey,
		color,
		sort,
		valueFormatter,
		...restProps
	}: WithoutChildren<WithElementRef<HTMLAttributes<HTMLDivElement>>> & {
		hideLabel?: boolean
		label?: string
		indicator?: 'line' | 'dot' | 'dashed'
		nameKey?: string
		/** Order of the tooltip rows */
		sort?: (a: TooltipPayload, b: TooltipPayload) => number
		/** Text for each row's value; defaults to `toLocaleString()` */
		valueFormatter?: (value: number, item: TooltipPayload) => string
		labelKey?: string
		hideIndicator?: boolean
		/** Hide each row's name, ex. when the chart has a single series */
		hideName?: boolean
		labelClassName?: string
		labelFormatter?:
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			((value: any, payload: TooltipPayload[]) => string | number | Snippet) | null
		formatter?: Snippet<
			[
				{
					value: unknown
					name: string
					item: TooltipPayload
					index: number
					payload: TooltipPayload[]
				},
			]
		>
	} = $props()

	const chart = useChart()
	const chartCtx = getChartContext()

	const payload = $derived(
		chartCtx.tooltip.series
			.filter(s => s.visible && s.value != null)
			.map((s): TooltipPayload => ({
				key: s.key,
				label: s.label,
				name: s.label,
				value: s.value,
				color: s.color,
				visible: s.visible,
				payload: chartCtx.tooltip.data,
			}))
	)

	const tooltipCtx = $derived({ payload: sort ? payload.toSorted(sort) : payload })

	const formattedLabel = $derived.by(() => {
		if (hideLabel || !tooltipCtx.payload?.length) return null

		const [item] = tooltipCtx.payload
		const key = labelKey ?? item?.label ?? item?.name ?? 'value'

		const itemConfig = getPayloadConfigFromPayload(chart.config, item, key)

		const value =
			!labelKey && typeof label === 'string'
				? (chart.config[label as keyof typeof chart.config]?.label ?? label)
				: (itemConfig?.label ?? item.label)

		if (value === undefined) return null
		if (!labelFormatter) return value
		return labelFormatter(value, tooltipCtx.payload)
	})

	const nestLabel = $derived(tooltipCtx.payload.length === 1 && indicator !== 'dot')
</script>

{#snippet TooltipLabel()}
	{#if formattedLabel}
		<div class={cn('font-medium', labelClassName)}>
			{#if typeof formattedLabel === 'function'}
				{@render formattedLabel()}
			{:else}
				{formattedLabel}
			{/if}
		</div>
	{/if}
{/snippet}

<TooltipPrimitive.Root variant="none">
	<div
		bind:this={ref}
		class={cn(
			'border-border/50 bg-background grid min-w-36 items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl',
			className
		)}
		{...restProps}
	>
		{#if !nestLabel}
			{@render TooltipLabel()}
		{/if}
		<div class="grid gap-1.5">
			{#each tooltipCtx.payload as item, i (item.key + i)}
				{const key = $derived(`${nameKey || item.key || item.name || 'value'}`)}
				{const itemConfig = $derived(getPayloadConfigFromPayload(chart.config, item, key))}
				{const indicatorColor = $derived(color || item.color)}
				<div
					class={cn(
						'[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:size-2.5',
						indicator === 'dot' && 'items-center'
					)}
				>
					{#if formatter && item.value !== undefined && item.name}
						{@render formatter({
							value: item.value,
							name: item.name,
							item,
							index: i,
							payload: tooltipCtx.payload,
						})}
					{:else}
						{#if itemConfig?.icon}
							<itemConfig.icon />
						{:else if itemConfig?.image}
							<img
								class="size-4 shrink-0"
								alt=""
								crossorigin="anonymous"
								height="16"
								src={itemConfig.image}
								width="16"
							/>
						{:else if !hideIndicator}
							<!-- eslint-disable svelte/no-inline-styles -->
							<div
								style:--color-bg={indicatorColor}
								style:--color-border={indicatorColor}
								class={cn('shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)', {
									'size-2.5': indicator === 'dot',
									'h-full w-1': indicator === 'line',
									'w-0 border-[1.5px] border-dashed bg-transparent': indicator === 'dashed',
									'my-0.5': nestLabel && indicator === 'dashed',
								})}
							></div>
							<!-- eslint-enable svelte/no-inline-styles -->
						{/if}
						<div
							class={cn(
								'flex flex-1 shrink-0 justify-between gap-4 leading-none',
								nestLabel ? 'items-end' : 'items-center'
							)}
						>
							{#if nestLabel || !hideName}
								<div class="grid gap-1.5">
									{#if nestLabel}
										{@render TooltipLabel()}
									{/if}
									{#if !hideName}
										<span class="text-muted-foreground">
											{itemConfig?.label || item.name}
										</span>
									{/if}
								</div>
							{/if}
							{#if item.value !== undefined}
								<span class="text-foreground font-mono font-medium tabular-nums">
									{valueFormatter ? valueFormatter(item.value, item) : item.value.toLocaleString()}
								</span>
							{/if}
						</div>
					{/if}
				</div>
			{/each}
		</div>
	</div>
</TooltipPrimitive.Root>
